import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Pencil, Trash2, X, Ticket, Eye } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  usePunchCardTemplates,
  useCreatePunchCardTemplate,
  useUpdatePunchCardTemplate,
  useDeletePunchCardTemplate,
  type PunchCardTemplate,
} from '@/hooks/usePunchCardTemplates';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SectionCard } from './ClinicGeneral';

interface TemplateFormData {
  name: string;
  description: string;
  total_clips: number;
  price_cents: number;
  currency: string;
  expires_months: number | null;
  display_order: number;
  is_active: boolean;
  visible_in_portal: boolean;
}

const DEFAULT_FORM: TemplateFormData = {
  name: '',
  description: '',
  total_clips: 10,
  price_cents: 0,
  currency: 'DKK',
  expires_months: 12,
  display_order: 0,
  is_active: true,
  visible_in_portal: false,
};

export function PunchCardTemplates() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();

  const canEdit = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));

  const { data: templates = [], isLoading, error } = usePunchCardTemplates(activeClinicId);
  const create = useCreatePunchCardTemplate();
  const update = useUpdatePunchCardTemplate();
  const remove = useDeletePunchCardTemplate();

  const [editing, setEditing] = useState<PunchCardTemplate | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<TemplateFormData>(DEFAULT_FORM);

  function openCreate() {
    setEditing(null);
    setForm(DEFAULT_FORM);
    setShowForm(true);
  }

  function openEdit(template: PunchCardTemplate) {
    setEditing(template);
    setForm({
      name: template.name,
      description: template.description ?? '',
      total_clips: template.total_clips,
      price_cents: template.price_cents,
      currency: template.currency,
      expires_months: template.expires_months,
      display_order: template.display_order,
      is_active: template.is_active,
      visible_in_portal: template.visible_in_portal,
    });
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditing(null);
    setForm(DEFAULT_FORM);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!activeClinicId) return;

    try {
      if (editing) {
        await update.mutateAsync({
          id: editing.id,
          ...form,
        });
        toast.success(t('patients.punch_cards.template_updated', 'Template updated'));
      } else {
        await create.mutateAsync({
          clinic_id: activeClinicId,
          appointment_types: [],
          group_types: [],
          ...form,
        });
        toast.success(t('patients.punch_cards.template_created', 'Template created'));
      }
      closeForm();
    } catch (err) {
      toast.error(
        editing
          ? t('patients.punch_cards.template_update_error', 'Failed to update template')
          : t('patients.punch_cards.template_create_error', 'Failed to create template'),
      );
    }
  }

  async function handleDelete(template: PunchCardTemplate) {
    if (!activeClinicId) return;
    if (
      !window.confirm(
        t('patients.punch_cards.confirm_delete_template', `Delete template "${template.name}"?`),
      )
    ) {
      return;
    }

    try {
      await remove.mutateAsync({ id: template.id, clinicId: activeClinicId });
      toast.success(t('patients.punch_cards.template_deleted', 'Template deleted'));
    } catch (err) {
      toast.error(t('patients.punch_cards.template_delete_error', 'Failed to delete template'));
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorAlert
        message={t('patients.punch_cards.templates_load_error', 'Failed to load templates')}
      />
    );
  }

  return (
    <div className="space-y-6">
      <SectionCard
        title={t('patients.punch_cards.templates', 'Punch Card Templates')}
        subtitle={t(
          'patients.punch_cards.templates_description',
          'Create reusable templates for quick punch card creation. Templates can be used by staff or made available for patients to purchase online.',
        )}
      >
        <div className="space-y-4">
          {canEdit && (
            <Button onClick={openCreate} variant="primary" disabled={showForm}>
              <Plus className="mr-2 h-4 w-4" />
              {t('patients.punch_cards.new_template', 'New Template')}
            </Button>
          )}

          {showForm && (
            <form
              onSubmit={onSubmit}
              className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {editing
                    ? t('patients.punch_cards.edit_template', 'Edit Template')
                    : t('patients.punch_cards.new_template', 'New Template')}
                </h3>
                <button
                  type="button"
                  onClick={closeForm}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Input
                    label={t('patients.punch_cards.name', 'Name')}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t('patients.punch_cards.name_placeholder', 'e.g., 10 Massage Sessions')}
                    required
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('patients.punch_cards.description', 'Description')}
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    rows={2}
                    placeholder={t('patients.punch_cards.description_placeholder', 'Optional description')}
                  />
                </div>

                <Input
                  type="number"
                  label={t('patients.punch_cards.total_clips', 'Total Clips')}
                  value={form.total_clips}
                  onChange={(e) => setForm({ ...form, total_clips: parseInt(e.target.value) || 0 })}
                  min={1}
                  max={100}
                  required
                />

                <Input
                  type="number"
                  label={t('patients.punch_cards.price_dkk', 'Price (DKK)')}
                  value={(form.price_cents / 100).toFixed(2)}
                  onChange={(e) =>
                    setForm({ ...form, price_cents: Math.round(parseFloat(e.target.value) * 100) })
                  }
                  min={0}
                  step="0.01"
                  required
                />

                <Input
                  type="number"
                  label={t('patients.punch_cards.expires_months', 'Valid for (months)')}
                  value={form.expires_months || ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      expires_months: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  min={1}
                  placeholder={t('patients.punch_cards.no_expiry', 'No expiry')}
                />

                <Input
                  type="number"
                  label={t('patients.punch_cards.display_order', 'Display Order')}
                  value={form.display_order}
                  onChange={(e) =>
                    setForm({ ...form, display_order: parseInt(e.target.value) || 0 })
                  }
                  min={0}
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('patients.punch_cards.is_active', 'Active')}
                  </span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.visible_in_portal}
                    onChange={(e) => setForm({ ...form, visible_in_portal: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('patients.punch_cards.visible_in_portal', 'Allow patients to purchase online')}
                  </span>
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={closeForm}>
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button type="submit" variant="primary" disabled={create.isPending || update.isPending}>
                  {create.isPending || update.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : editing ? (
                    t('common.save', 'Save')
                  ) : (
                    t('common.create', 'Create')
                  )}
                </Button>
              </div>
            </form>
          )}

          {templates.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-12 text-center dark:border-gray-700">
              <Ticket className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                {t('patients.punch_cards.no_templates', 'No templates yet')}
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('patients.punch_cards.no_templates_hint', 'Create your first template to get started')}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Ticket className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      <h4 className="font-medium text-gray-900 dark:text-white">{template.name}</h4>
                      {!template.is_active && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                          {t('common.inactive', 'Inactive')}
                        </span>
                      )}
                      {template.visible_in_portal && (
                        <span className="flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          <Eye className="h-3 w-3" />
                          {t('patients.punch_cards.online', 'Online')}
                        </span>
                      )}
                    </div>
                    {template.description && (
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {template.description}
                      </p>
                    )}
                    <div className="mt-2 flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <span>
                        {template.total_clips} {t('patients.punch_cards.clips', 'clips')}
                      </span>
                      <span>{(template.price_cents / 100).toFixed(0)} {template.currency}</span>
                      {template.expires_months && (
                        <span>
                          {t('patients.punch_cards.valid_for', 'Valid for')} {template.expires_months}{' '}
                          {t('common.months', 'months')}
                        </span>
                      )}
                    </div>
                  </div>

                  {canEdit && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openEdit(template)}
                        disabled={showForm}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(template)}
                        disabled={remove.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
